import Switch from "@app/components/switch";
import {
  BgScreenThemeClass,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  READING_THEME_KEY,
  ReadingScreenTheme,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import BookService from "@app/services/http/book.service";
import {
  BookAudioDetail,
  BookDetail,
  BookElementChildrenDetail,
  BookElementDetail,
  BookElementHighlightTextDetail,
  Comment,
} from "@app/types";
import { Images } from "@assets/images";
import StorageService from "@core/services/storage";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CommentItem from "./components/comment";
import CommentService from "@app/services/http/comment.service";
import { RequestParams } from "@core/services/http/http.type";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { Form, Formik, FormikHelpers } from "formik";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Button from "@app/components/button";
import ConfirmModal from "@app/components/confirm-modal";
import { IoClose } from "react-icons/io5";
import { TbEdit } from "react-icons/tb";
import { openPortalDialog } from "@app/services/modal.service";
import BookNoteModal from "@app/components/book-note-modal/book-note-modal.component";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { addToast } from "@app/components/toast/toast.service";
import { useTextSelection } from "@app/hooks/use-text-selection.hook";
import BookNoteService from "@app/services/http/book-note.service";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth } from "@app/store/auth";
import {
  AudiobookData,
  selectAudiobook,
  sendAudiobook,
  showAudiobookPanel,
} from "@app/store/audiobook";
import PurchaseMediaConfirmModal from "@app/components/purchase-media-confirm-modal";
import PurchaseService from "@app/services/http/purchase.service";
import { BookMediaType } from "@app/types";

const highlightText = (
  textSelect: string,
  content: string,
  activeListeningText = ""
): string => {
  if (!content) return "";

  if (!textSelect && !activeListeningText) return content;

  const escapedTextSelect = textSelect.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const escapedActiveListeningText = activeListeningText.replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&"
  );

  const textSelectRegex = escapedTextSelect
    ? new RegExp(`(${escapedTextSelect})`, "gi")
    : null;
  const activeListeningTextRegex = escapedActiveListeningText
    ? new RegExp(`(${escapedActiveListeningText})`, "gi")
    : null;

  let result = content;

  if (textSelectRegex) {
    result = result.replace(
      textSelectRegex,
      (match) =>
        `<span style="background-color: #E1EAF8; font-size: inherit; font-family: inherit;">${match}</span>`
    );
  }

  if (activeListeningTextRegex) {
    result = result.replace(
      activeListeningTextRegex,
      (match) =>
        `<span style="background-color: #D6E4FF; color: #1e3a8a; font-size: inherit; font-family: inherit;">${match}</span>`
    );
  }

  return result;
};

const sortHighlightTexts = (
  items: BookElementHighlightTextDetail[] = []
): BookElementHighlightTextDetail[] =>
  [...items].sort((a, b) => (a.duration ?? 0) - (b.duration ?? 0));

const formatDurationFromMs = (value?: number): string => {
  const totalSeconds = Math.max(0, Math.floor((value ?? 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${mm}:${ss}`;
  }

  return `${mm}:${ss}`;
};

const chapterAudioPlaybackRates = [
  { value: 0.25, label: "0,25x" },
  { value: 0.5, label: "0,5x" },
  { value: 0.75, label: "0,75x" },
  { value: 1, label: "Bình thường" },
  { value: 1.25, label: "1,25x" },
  { value: 1.5, label: "1,5x" },
  { value: 1.75, label: "1,75x" },
  { value: 2, label: "2x" },
];

type ReadingChapterItem = BookElementDetail | BookElementChildrenDetail;

const toReadingChapterItem = (item: ReadingChapterItem): BookElementDetail => ({
  id: item.id,
  name: item.name,
  description: item.description ?? null,
  content: item.content ?? "",
  linkAudio: item.linkAudio ?? "",
  level: item.level ?? 0,
  order: item.order ?? 0,
  child: item.child ?? [],
  isLock: Boolean(item.isLock),
  point: Number(item.point ?? 0),
  bookNotes: item.bookNotes ?? [],
  audioDuration: item.audioDuration,
  audios: item.audios ?? [],
  highlightTexts: item.highlightTexts ?? [],
});

const flattenBookElements = (
  elements: BookElementDetail[] = []
): BookElementDetail[] => {
  const outputs: BookElementDetail[] = [];

  const traverse = (item: ReadingChapterItem) => {
    outputs.push(toReadingChapterItem(item));
    (item.child ?? []).forEach((child) => {
      traverse(child);
    });
  };

  elements.forEach((item) => {
    traverse(item);
  });

  return outputs;
};

const getDisplayAuthor = (book?: BookDetail): string => {
  if (!book) return "";

  if ((book as any).authors?.length) {
    return (book as any).authors.map((a: any) => a.fullName).filter(Boolean).join(", ");
  }

  return (book as any).author ?? "";
};

const getElementAudioLink = (item: BookElementDetail): string => {
  if ((item as any).linkAudio) return (item as any).linkAudio;

  const found = item.audios?.find((a) => !!(a as any).audio);

  return (found as any)?.audio ?? "";
};

function Reading() {
  const backgroundAudioRef = useRef<HTMLAudioElement>(null);
  const chapterAudioRef = useRef<HTMLAudioElement>(null);
  const [searchParams] = useSearchParams();

  const { id } = useParams();
  const chapterParam = Number(searchParams.get("chapter"));
  const chapter = Number.isNaN(chapterParam) ? 0 : chapterParam;

  const [loading, setLoading] = useState(true);
  const [book, setBook] = useState<BookDetail>();
  const [isSettingShow, setIsSettingShow] = useState(false);
  const [isListViewShow, setIsListViewShow] = useState(false);
  const [isCommentShow, setIsCommentShow] = useState(false);
  const [isBgAudioPlaying, setIsBgAudioPlaying] = useState(false);
  const [isChapterAudioPlaying, setIsChapterAudioPlaying] = useState(false);
  const [chapterSelected, setChapterSelected] = useState<BookElementDetail>();
  const [voiceSelectedIndex, setVoiceSelectedIndex] = useState(0);
  const [chapterAudioPlaybackRate, setChapterAudioPlaybackRate] = useState(1);
  const [chapterAudioCurrentTimeMs, setChapterAudioCurrentTimeMs] = useState(0);
  const [backgroundAudioLink, setBackgroundAudioLink] = useState<string>();
  const [theme, setTheme] = useState(ReadingScreenTheme.DEFAULT);
  const { abortController } = useCancellationRequest();
  const [comment, setComment] = useState<Comment[]>([]);
  const [editComment, setEditComment] = useState("");
  const [reply, setReply] = useState(false);
  const [requestParams, setRequestParams] = useState<RequestParams>({
    ...DEFAULT_REQUEST_PARAMS,
    pageSize: 50,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectAuth);
  const audiobookState = useSelector(selectAudiobook);

  const iconRef = useRef<HTMLDivElement>(null);
  const playbackRateRef = useRef<HTMLDivElement>(null);
  const [update, forceUpdate] = useForceUpdate();
  const [typeSideBarList, setTypeSibarList] = useState<string>("chapter");
  const [isPlaybackRateShow, setIsPlaybackRateShow] = useState(false);

  const { clientRect, textContent } = useTextSelection(iconRef);
  const flattenedBookElements = useMemo(
    () => flattenBookElements(book?.elements),
    [book?.elements]
  );

  useEffect(() => {
    const previousTheme = StorageService.get(READING_THEME_KEY);

    if (
      !isNaN(Number(previousTheme)) &&
      Number(previousTheme) >= ReadingScreenTheme.DEFAULT &&
      Number(previousTheme) <= ReadingScreenTheme.DARK
    ) {
      setTheme(Number(previousTheme));
    }
  }, []);

  useEffect(() => {
    if (!isNaN(Number(id))) {
      BookService.getBookById(Number(id), undefined, {
        useCache: true,
        forceRefresh: update > 0,
      })
        .then((res) => {
          setBook(res.data);

          if (res.data.backgroundMusic.link) {
            setBackgroundAudioLink(res.data.backgroundMusic.link);
          }

          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      navigate(RoutePath.HOME);
    }
  }, [id, update]);

  useEffect(() => {
    if (!flattenedBookElements.length) return;

    const selectedChapter =
      flattenedBookElements[chapter] ??
      flattenedBookElements.find((item) => item.order === chapter + 1) ??
      flattenedBookElements[0];

    setChapterSelected(selectedChapter);
  }, [flattenedBookElements, chapter]);

  useEffect(() => {
    if (!chapterSelected) return;

    setVoiceSelectedIndex(0);
  }, [chapterSelected?.id]);

  const chapterAudios = useMemo((): BookAudioDetail[] => {
    if (!chapterSelected) return [];

    const outputs =
      chapterSelected.audios?.filter((item) => !!item.audio?.trim()) ?? [];

    if (outputs.length) {
      return outputs;
    }

    if (chapterSelected.linkAudio) {
      return [
        {
          name: "Mặc định",
          audio: chapterSelected.linkAudio,
        },
      ];
    }

    return [];
  }, [chapterSelected]);

  const chapterAudioLink = useMemo(() => {
    if (!chapterAudios.length) return "";

    return (
      chapterAudios[voiceSelectedIndex]?.audio ??
      chapterAudios[0]?.audio ??
      ""
    );
  }, [chapterAudios, voiceSelectedIndex]);

  const chapterHighlightTexts = useMemo(
    () => sortHighlightTexts(chapterSelected?.highlightTexts ?? []),
    [chapterSelected?.highlightTexts]
  );

  const activeHighlightIndex = useMemo(() => {
    if (!chapterHighlightTexts.length) return -1;

    let index = -1;

    chapterHighlightTexts.forEach((item, itemIndex) => {
      if ((item.duration ?? 0) <= chapterAudioCurrentTimeMs) {
        index = itemIndex;
      }
    });

    return index;
  }, [chapterHighlightTexts, chapterAudioCurrentTimeMs]);

  const activeHighlightText =
    activeHighlightIndex >= 0
      ? chapterHighlightTexts[activeHighlightIndex]?.text ?? ""
      : "";

  useEffect(() => {
    if (!chapterAudioRef.current) return;

    chapterAudioRef.current.load();
    chapterAudioRef.current.playbackRate = chapterAudioPlaybackRate;
    setChapterAudioCurrentTimeMs(0);

    if (isChapterAudioPlaying && chapterAudioLink) {
      chapterAudioRef.current
        .play()
        .then(() => setIsChapterAudioPlaying(true))
        .catch(() => setIsChapterAudioPlaying(false));
    }
  }, [chapterAudioLink]);

  useEffect(() => {
    if (!chapterAudioRef.current) return;

    chapterAudioRef.current.playbackRate = chapterAudioPlaybackRate;
  }, [chapterAudioPlaybackRate, chapterAudioLink]);

  useEffect(() => {
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.load();
    }
  }, [backgroundAudioLink]);

  useEffect(() => {
    if (isCommentShow) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isCommentShow]);

  const handleBackClick = () => {
    navigate(`/${RoutePath.BOOK_DETAIL}/${Number(id)}`);
  };

  const handleListenBookClick = () => {
    if (!book?.audio?.enabled) {
      return;
    }

    if (!book.audio.hasAccess) {
      if (book.audio.canPurchase) {
        const currentKas = Number(userInfo.point ?? 0);
        const requiredKas = Number(book.audio.price ?? 0);
        const purchaseModal = openPortalDialog(PurchaseMediaConfirmModal, {
          bookName: book.name,
          mediaLabel: "Audio",
          currentKas,
          requiredKas,
        });

        purchaseModal.afterClosed().subscribe((data) => {
          if (!data?.isAccept) {
            return;
          }

          if (!data.isEnoughKas) {
            navigate(RoutePath.VAS_OPTION_LIST);

            return;
          }

          PurchaseService.purchaseBookByMediaType(book.id, BookMediaType.AUDIO).then(() => {
            addToast({ text: "Mua audio thành công" });
            BookService.getBookById(Number(id)).then((res) => setBook(res.data));
          });
        });

        return;
      }

      navigate(`/${RoutePath.BOOK_DETAIL}/${Number(id)}`);

      return;
    }

    if (audiobookState.audiobookData.bookId === book.id) {
      dispatch(showAudiobookPanel());

      return;
    }

    const audiobookData: AudiobookData = {
      bookId: book.id,
      bookName: book.name,
      author: getDisplayAuthor(book),
      thumbnail: (book as any).thumbnails?.[0],
      audioList:
        book.elements
          ?.filter((item) => !!getElementAudioLink(item))
          ?.map((item) => ({
            id: item.id!,
            name: item.name,
            link: getElementAudioLink(item),
          })) ?? [],
    };

    dispatch(showAudiobookPanel());
    dispatch(sendAudiobook(audiobookData));
  };

  const handleSettingClick = () => {
    setIsSettingShow((prev) => {
      const next = !prev;

      if (!next) {
        setIsPlaybackRateShow(false);
      }

      return next;
    });
  };

  const handleListViewClick = () => {
    setIsListViewShow(!isListViewShow);
  };

  const handleCommentClick = () => {
    setIsCommentShow(!isCommentShow);
  };

  const handleNightModeClick = () => {
    if (theme === ReadingScreenTheme.DARK) {
      setTheme(ReadingScreenTheme.DEFAULT);
      StorageService.set(READING_THEME_KEY, ReadingScreenTheme.DEFAULT);
    } else {
      setTheme(ReadingScreenTheme.DARK);
      StorageService.set(READING_THEME_KEY, ReadingScreenTheme.DARK);
    }
  };

  const isPreviousDisabled = () => {
    if (!chapterSelected) return false;

    const currentChapter = flattenedBookElements.findIndex(
      (item) => item.id === chapterSelected.id
    );

    return currentChapter !== -1 && currentChapter <= 0;
  };

  const isNextDisabled = () => {
    if (!chapterSelected) return false;

    const currentChapterIndex = flattenedBookElements.findIndex(
      (item) => item.id === chapterSelected.id
    );

    const nextChapter = flattenedBookElements[currentChapterIndex + 1];

    if (!nextChapter) return true;

    return (
      (currentChapterIndex !== -1 &&
        currentChapterIndex >= flattenedBookElements.length - 1) ||
      nextChapter.isLock
    );
  };

  const handlePreviousClick = () => {
    if (!chapterSelected) return;

    const currentChapter = flattenedBookElements.findIndex(
      (item) => item.id === chapterSelected.id
    );

    if (currentChapter !== -1 && currentChapter > 0) {
      setChapterSelected(flattenedBookElements[currentChapter - 1]);
    }
  };

  const handleNextClick = () => {
    if (!chapterSelected) return;

    const currentChapterIndex = flattenedBookElements.findIndex(
      (item) => item.id === chapterSelected.id
    );
    const nextChapter = flattenedBookElements[currentChapterIndex + 1];

    if (!nextChapter) return;

    if (
      currentChapterIndex !== -1 &&
      currentChapterIndex < flattenedBookElements.length - 1 &&
      !nextChapter.isLock
    ) {
      setChapterSelected(flattenedBookElements[currentChapterIndex + 1]);
    }
  };

  const handleChapterSelect = (item: BookElementDetail) => {
    setChapterSelected(item);
    setIsListViewShow(false);
  };

  const handleAudioToggle = () => {
    if (!chapterAudioRef.current || !chapterAudioLink) return;

    if (isChapterAudioPlaying) {
      chapterAudioRef.current.pause();
      setIsChapterAudioPlaying(false);

      if (backgroundAudioRef.current && isBgAudioPlaying) {
        backgroundAudioRef.current.pause();
        setIsBgAudioPlaying(false);
      }

      return;
    }

    chapterAudioRef.current.playbackRate = chapterAudioPlaybackRate;
    chapterAudioRef.current
      .play()
      .then(() => setIsChapterAudioPlaying(true))
      .catch(() => setIsChapterAudioPlaying(false));

    if (backgroundAudioRef.current && backgroundAudioLink) {
      backgroundAudioRef.current.volume = 0.7;
      backgroundAudioRef.current
        .play()
        .then(() => setIsBgAudioPlaying(true))
        .catch(() => setIsBgAudioPlaying(false));
    }
  };

  const setBackgroundColor = (theme: ReadingScreenTheme) => {
    setTheme(theme);
    StorageService.set(READING_THEME_KEY, theme);
  };

  const handleBackgroundAudioToggle = () => {
    if (!backgroundAudioRef.current || !backgroundAudioLink) return;

    if (isBgAudioPlaying) {
      backgroundAudioRef.current.pause();
      setIsBgAudioPlaying(false);
    } else {
      backgroundAudioRef.current
        .play()
        .then(() => setIsBgAudioPlaying(true))
        .catch(() => setIsBgAudioPlaying(false));
    }
  };

  const handleJumpToHighlight = (item: BookElementHighlightTextDetail) => {
    if (!chapterAudioRef.current || !chapterAudioLink) return;

    const nextTimeMs = Math.max(0, Number(item.duration ?? item.from ?? 0));

    chapterAudioRef.current.currentTime = nextTimeMs / 1000;
    setChapterAudioCurrentTimeMs(nextTimeMs);
    chapterAudioRef.current.playbackRate = chapterAudioPlaybackRate;
    chapterAudioRef.current
      .play()
      .then(() => setIsChapterAudioPlaying(true))
      .catch(() => setIsChapterAudioPlaying(false));
  };

  const handleSeekChapterAudio = (offsetSeconds: number) => {
    if (!chapterAudioRef.current) return;

    const currentTime = chapterAudioRef.current.currentTime || 0;
    const maxTime = Number.isFinite(chapterAudioRef.current.duration)
      ? chapterAudioRef.current.duration
      : currentTime;
    const nextTime = Math.min(maxTime, Math.max(0, currentTime + offsetSeconds));

    chapterAudioRef.current.currentTime = nextTime;
    setChapterAudioCurrentTimeMs(nextTime * 1000);
  };

  const handleBackward10Seconds = () => {
    handleSeekChapterAudio(-10);
  };

  const handleForward10Seconds = () => {
    handleSeekChapterAudio(10);
  };

  const handleChapterAudioPlaybackRateSelect = (nextRate: number) => {
    if (!Number.isFinite(nextRate) || nextRate <= 0) return;

    setChapterAudioPlaybackRate(nextRate);
    setIsPlaybackRateShow(false);
  };

  const handleChapterAudioPlaybackRateClick = () => {
    setIsPlaybackRateShow((prev) => !prev);
  };

  const chapterAudioPlaybackRateLabel =
    chapterAudioPlaybackRates.find(
      (item) => item.value === chapterAudioPlaybackRate
    )?.label ?? "Bình thường";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        playbackRateRef.current &&
        !playbackRateRef.current.contains(event.target as Node)
      ) {
        setIsPlaybackRateShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!chapterAudioRef.current) return;

    const audio = chapterAudioRef.current;
    const handleTimeUpdate = () => {
      setChapterAudioCurrentTimeMs(audio.currentTime * 1000);
    };
    const handlePause = () => {
      setIsChapterAudioPlaying(false);
    };
    const handlePlay = () => {
      setIsChapterAudioPlaying(true);
    };
    const handleEnded = () => {
      setIsChapterAudioPlaying(false);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  useEffect(() => {
    const bookId = id ?? "";

    const newRequestParams: RequestParams = {
      ...requestParams,
    };

    CommentService.getComment(bookId, newRequestParams, abortController.signal)
      .then((res) => {
        setComment(res.data.items);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, requestParams, update, reply, editComment]);

  const onSubmit = (
    values: { content: string },
    { setFieldValue }: FormikHelpers<{ content: string }>
  ) => {
    if (!values.content) return;

    CommentService.create({
      bookId: Number(id),
      content: values.content,
    }).then(() => {
      addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
      setFieldValue("content", "");
      forceUpdate();
    });
  };

  const handleLikeComment = (commentId: number) => {
    const bookId = id ?? "";
    const newRequestParams: RequestParams = { ...requestParams };

    CommentService.likeComment(commentId).then(() => {
      CommentService.getComment(
        bookId,
        newRequestParams,
        abortController.signal
      )
        .then((res) => {
          setComment(res.data.items);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const handleDeleteComment = (commentId: number) => {
    if (commentId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_COMMENT_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          CommentService.deleteComment(commentId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });

            forceUpdate();
          });
        }
      });
    }
  };

  const handleReply = (onReply: boolean) => {
    if (onReply === reply) {
      setReply((prev) => !prev);
    } else {
      setReply(onReply);
    }
  };

  const handleEditComment = (content: string) => {
    if (content === editComment) {
      setEditComment(""); // Reset the comment if it is the same
    } else {
      setEditComment(content); // Set the comment to the new content
    }
  };

  const handleNote = () => {
    if (!chapterSelected || !textContent) return;

    const bookNoteModallObs = openPortalDialog(BookNoteModal);

    bookNoteModallObs.afterClosed().subscribe((data) => {
      if (!data?.textNote) return;

      BookNoteService.createNote({
        textNote: data.textNote,
        elementId: chapterSelected.id,
        textSelected: textContent,
      }).then(() => {
        addToast({ text: SystemMessage.CREATE_BOOK_NOTE_SUCESS });
        forceUpdate();
      });
    });
  };

  const highlightedContent = useMemo(() => {
    return highlightText(
      textContent || "",
      chapterSelected?.content ?? "",
      activeHighlightText
    );
  }, [
    textContent,
    chapterSelected?.content,
    activeHighlightText,
  ]);

  return (
    <div>
      <div className="h-[72px] pl-8 pr-6 fixed top-0 left-0 w-full bg-[#272C36] flex items-center justify-between z-50">
        <div
          className="size-6 rounded-full hover:bg-gray-500 cursor-pointer flex items-center justify-center"
          onClick={handleBackClick}
        >
          <img src={Images.ArrowThinLeftIcon.default} alt="" />
        </div>
        <div>
          {!loading ? (
            <div className="text-white font-[SVN-Gilroy] font-bold text-xl">
              {chapterSelected?.name}
            </div>
          ) : (
            <Skeleton className="w-40 h-7" />
          )}
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-1">
            <img
              className="size-9 p-[6px] cursor-pointer hover:rounded-full hover:bg-gray-500"
              src={Images.BackwardIcon.default}
              alt=""
              onClick={handleBackward10Seconds}
            />
            <img
              className="cursor-pointer hover:rounded-full hover:bg-gray-500"
              src={
                !isChapterAudioPlaying
                  ? Images.AudioIcon.default
                  : Images.AudioEnabledIcon.default
              }
              alt=""
              onClick={handleAudioToggle}
            />
            <img
              className="size-9 p-[6px] cursor-pointer hover:rounded-full hover:bg-gray-500"
              src={Images.ForwardIcon.default}
              alt=""
              onClick={handleForward10Seconds}
            />
          </div>
          <div>
            <img
              className="cursor-pointer hover:rounded-full hover:bg-gray-500 size-6"
              src={
                !isListViewShow
                  ? Images.ListViewCircle.default
                  : Images.ListViewShowing.default
              }
              alt=""
              onClick={handleListViewClick}
            />
          </div>
          <div>
            <img
              className="cursor-pointer hover:rounded-full hover:bg-gray-500 size-6"
              src={
                theme !== ReadingScreenTheme.DARK
                  ? Images.MoonIcon.default
                  : Images.MoonEnabledIcon.default
              }
              alt=""
              onClick={handleNightModeClick}
            />
          </div>
          <div className="relative">
            <img
              className="cursor-pointer hover:rounded-full hover:bg-gray-500 size-6"
              src={
                !isSettingShow
                  ? Images.SettingIcon.default
                  : Images.SettingEnabledIcon.default
              }
              alt=""
              onClick={handleSettingClick}
            />
            <div
              className={clsx(
                "absolute z-10 top-9 -right-5 text-white w-[296px] p-5 rounded-xl bg-[#171A20] border border-solid border-[#272C36] hidden",
                {
                  "!block": !!isSettingShow,
                }
              )}
            >
              <div className="font-[SVN-Gilroy] font-semibold tracking-[0.024px]">
                Màu nền
              </div>
              <div className="mt-3 flex justify-between">
                <div
                  className={clsx(
                    "w-14 h-9 rounded-2xl bg-[#F5F2EE] cursor-pointer",
                    {
                      "border-[2px] border-solid border-[#38499b]":
                        theme === ReadingScreenTheme.DEFAULT,
                    }
                  )}
                  onClick={() => setBackgroundColor(ReadingScreenTheme.DEFAULT)}
                ></div>
                <div
                  className={clsx(
                    "w-14 h-9 rounded-2xl bg-[#ABC2BA] cursor-pointer",
                    {
                      "border-[2px] border-solid border-[#38499b]":
                        theme === ReadingScreenTheme.GREEN,
                    }
                  )}
                  onClick={() => setBackgroundColor(ReadingScreenTheme.GREEN)}
                ></div>
                <div
                  className={clsx(
                    "w-14 h-9 rounded-2xl bg-[#E0DCCE] cursor-pointer",
                    {
                      "border-[2px] border-solid border-[#38499b]":
                        theme === ReadingScreenTheme.BROWN,
                    }
                  )}
                  onClick={() => setBackgroundColor(ReadingScreenTheme.BROWN)}
                ></div>
                <div
                  className={clsx(
                    "w-14 h-9 rounded-2xl bg-[#272C36] flex items-center justify-center cursor-pointer",
                    {
                      "border-[2px] border-solid border-[#38499b]":
                        theme === ReadingScreenTheme.DARK,
                    }
                  )}
                  onClick={() => setBackgroundColor(ReadingScreenTheme.DARK)}
                >
                  <img src={Images.MoonBoldIcon.default} alt="" />
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div className="font-[SVN-Gilroy] font-semibold tracking-[0.024px]">
                  Nhạc nền
                </div>
                <Switch
                  checked={isBgAudioPlaying}
                  onChange={handleBackgroundAudioToggle}
                />
              </div>
              <div className="mt-5">
                <div className="font-[SVN-Gilroy] font-semibold tracking-[0.024px] mb-2">
                  Giọng đọc
                </div>
                <select
                  className="w-full h-10 rounded-lg bg-[#272C36] border border-[#4E586C] px-3 text-sm text-white outline-none"
                  value={voiceSelectedIndex}
                  onChange={(event) =>
                    setVoiceSelectedIndex(Number(event.target.value) || 0)
                  }
                  disabled={chapterAudios.length <= 1}
                >
                  {chapterAudios.map((item, index) => (
                    <option key={`${item.audio}-${index}`} value={index}>
                      {item.name || `Giọng ${index + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mt-5">
                <div className="font-[SVN-Gilroy] font-semibold tracking-[0.024px] mb-2">
                  Tốc độ phát
                </div>
                <div className="relative" ref={playbackRateRef}>
                  <div
                    className={clsx(
                      "w-full h-10 rounded-lg bg-[#272C36] border border-[#4E586C] px-3 text-sm text-white outline-none flex items-center justify-between cursor-pointer",
                      {
                        "border-[#5A82D7]": isPlaybackRateShow,
                      }
                    )}
                    onClick={handleChapterAudioPlaybackRateClick}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        className="size-5"
                        src={Images.ListenAudioSpeedIcon.default}
                        alt=""
                      />
                      <span className="font-[SVN-Gilroy]">
                        {chapterAudioPlaybackRateLabel}
                      </span>
                    </div>
                    <img
                      className={clsx("size-4 transition-transform", {
                        "rotate-180": isPlaybackRateShow,
                      })}
                      src={Images.ArrowDown.default}
                      alt=""
                    />
                  </div>
                  <div
                    className={clsx(
                      "absolute right-0 top-11 w-full rounded-lg bg-[#171A20] border border-solid border-[#272C36] p-2 hidden z-20",
                      {
                        "!block": isPlaybackRateShow,
                      }
                    )}
                  >
                    <div className="flex flex-col">
                      {chapterAudioPlaybackRates.map((item) => (
                        <div
                          key={item.value}
                          className={clsx(
                            "px-2 py-2 text-sm font-[SVN-Gilroy] cursor-pointer rounded-md hover:bg-[#272C36]",
                            {
                              "text-[#5A82D7]":
                                item.value === chapterAudioPlaybackRate,
                              "text-white":
                                item.value !== chapterAudioPlaybackRate,
                            }
                          )}
                          onClick={() =>
                            handleChapterAudioPlaybackRateSelect(item.value)
                          }
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={clsx("min-h-[calc(100vh-72px)] relative", {
          [BgScreenThemeClass[ReadingScreenTheme.DEFAULT]]:
            theme === ReadingScreenTheme.DEFAULT,
          [BgScreenThemeClass[ReadingScreenTheme.GREEN]]:
            theme === ReadingScreenTheme.GREEN,
          [BgScreenThemeClass[ReadingScreenTheme.BROWN]]:
            theme === ReadingScreenTheme.BROWN,
          [BgScreenThemeClass[ReadingScreenTheme.DARK]]:
            theme === ReadingScreenTheme.DARK,
        })}
      >
        <div className="fixed w-[44px] h-[368px] top-[30%] flex flex-col justify-around right-[5%] md:right-[5%] lg:right-[5%] xl:right-[10%]">
          <div className="cursor-pointer flex flex-col items-center">
            <div>
              <img src={Images.HeartGrayIcon.default} alt="" />
            </div>
            <div className="text-[#333B47] font-[SVN-Gilroy] text-sm font-medium">
              {book?.insight?.likeCount ?? 0}
            </div>
          </div>
          <div
            className="cursor-pointer flex flex-col items-center"
            onClick={handleCommentClick}
          >
            <div>
              <img src={Images.CommentIcon.default} alt="" />
            </div>
            <div className="text-[#333B47] font-[SVN-Gilroy] text-sm font-medium">
              {book?.insight?.commentCount ?? 0}
            </div>
          </div>
          <div className="cursor-pointer flex flex-col items-center">
            <div>
              <img src={Images.View.default} alt="" />
            </div>
            <div className="text-[#333B47] font-[SVN-Gilroy] text-sm font-medium">
              {book?.insight?.viewCount ?? 0}
            </div>
          </div>
          <div
            className={clsx("cursor-pointer flex flex-col items-center", {
              "cursor-not-allowed opacity-40": !book?.audio?.enabled,
            })}
            onClick={handleListenBookClick}
          >
            <div>
              <img src={Images.HeadPhoneDetail.default} alt="" />
            </div>
            <div className="text-[#333B47] font-[SVN-Gilroy] text-sm font-medium">
              {book?.insight?.listenCount ?? 0}
            </div>
          </div>
          <div className="cursor-pointer flex flex-col items-center">
            <div>
              <img className="=" src={Images.Share.default} alt="" />
            </div>
            <div className="text-[#333B47] font-[SVN-Gilroy] text-sm font-medium">
              {book?.insight?.shareCount ?? 0}
            </div>
          </div>
        </div>
        <div className="fixed left-8 top-1/2 h-[calc(100vh-72px)]">
          <img
            className={clsx("cursor-pointer", {
              "!cursor-not-allowed": isPreviousDisabled(),
            })}
            src={Images.LeftArrowIcon.default}
            alt=""
            onClick={handlePreviousClick}
          />
        </div>
        <div className="lg:w-[818px] md:w-[600px] pt-24 pb-5 mx-auto">
          {!loading ? (
            <div
              onContextMenu={(e) => {
                e.preventDefault();
              }}
              onCopy={(e) => {
                e.preventDefault();
              }}
              className="w-full selection:bg-[#E1EAF8]"
              dangerouslySetInnerHTML={{
                __html: highlightedContent,
              }}
            ></div>
          ) : (
            <Skeleton className="h-full" count={30} />
          )}
          {!!textContent?.length && clientRect && (
            <div
              ref={iconRef}
              data-tip="Chỉnh sửa đoạn văn bản"
              className="absolute bg-[#5A82D7] size-9 rounded-full cursor-pointer flex items-center justify-center"
              style={{
                top:
                  clientRect.y - 72 > 40
                    ? clientRect.y + window.scrollY - 40
                    : clientRect.y + window.scrollY + 20,
                left: clientRect.x + window.scrollX,
              }}
              onClick={handleNote}
            >
              <TbEdit
                className="text-white text-xl"
                onMouseDown={(e) => e.preventDefault()}
              />
            </div>
          )}
        </div>
        <div className="fixed right-8 top-1/2 h-[calc(100vh-72px)]">
          <img
            className={clsx("cursor-pointer", {
              "!cursor-not-allowed": isNextDisabled(),
            })}
            src={Images.RightArrowIcon.default}
            alt=""
            onClick={handleNextClick}
          />
        </div>
      </div>
      <div
        className={clsx(
          "fixed -right-[296px] top-[72px] w-[296px] overscroll-contain transition-all bg-[#272C36] h-[calc(100vh-72px)] max-h-[calc(100vh-72px)] overflow-y-auto",
          {
            "!right-0": !!isListViewShow,
          }
        )}
      >
        <div className="relative font-[SVN-Gilroy text-[#171A20] pt-5 p-6 pb-4 z-10">
          <div className="flex justify-between items-center text-[20px] font-bold text-white">
            Danh sách{" "}
            <IoClose className="cursor-pointer" onClick={handleListViewClick} />
          </div>
          <div className="flex gap-[16px] mt-2">
            <p
              className={clsx(
                "text-[16px] font-semibold",
                typeSideBarList === "chapter"
                  ? "text-[#5A82D7]"
                  : "text-[#FFF] cursor-pointer"
              )}
              onClick={() => setTypeSibarList("chapter")}
            >
              Mục lục
            </p>
            <p
              className={clsx(
                "text-[16px] font-semibol",
                typeSideBarList === "note"
                  ? "text-[#5A82D7]"
                  : "text-[#FFF] cursor-pointer"
              )}
              onClick={() => setTypeSibarList("note")}
            >
              Ghi chú
            </p>
            <p
              className={clsx(
                "text-[16px] font-semibol",
                typeSideBarList === "highlight"
                  ? "text-[#5A82D7]"
                  : "text-[#FFF] cursor-pointer"
              )}
              onClick={() => setTypeSibarList("highlight")}
            >
              Lời dẫn
            </p>
          </div>
          <hr className="absolute w-[260px] left-[18px] bottom-1 border-t-[#4E586C]" />
        </div>
        <div className="px-6 py-2">
          {!!flattenedBookElements.length &&
            typeSideBarList === "chapter" &&
            flattenedBookElements.map((item) => (
              <div
                key={item.id}
                className={clsx(
                  "py-2 hover:bg-gray-500 cursor-pointer rounded-lg font-[SVN-Gilroy] text-[#DFE2E7] font-medium tracking-[0.024px]",
                  {
                    "!text-[#38499b]": item.id === chapterSelected?.id,
                  }
                )}
                style={{ paddingLeft: Math.max(0, (item.level - 1) * 16) }}
                onClick={() => handleChapterSelect(item)}
              >
                {item.order}. {item.name}
              </div>
            ))}

          {!!chapterSelected?.bookNotes?.length &&
            typeSideBarList === "note" &&
            chapterSelected.bookNotes.map((item) => (
              <div key={item.id} className="py-1">
                <p className="font-medium text-[14px] text-[#DFE2E7]">
                  {item.textSelected}
                </p>
                <p className="font-medium text-[14px] text-[#B0BAC9]">
                  {item.textNote}
                </p>
              </div>
            ))}

          {!!chapterHighlightTexts.length &&
            typeSideBarList === "highlight" &&
            chapterHighlightTexts.map((item, index) => (
              <div
                key={`${item.duration}-${item.text}-${index}`}
                className={clsx(
                  "py-2 px-2 rounded-md cursor-pointer hover:bg-[#4E586C]",
                  {
                    "bg-[#3D4C65]": index === activeHighlightIndex,
                  }
                )}
                onClick={() => handleJumpToHighlight(item)}
              >
                <div className="text-[#8BB3FF] font-[SVN-Gilroy] text-xs font-semibold">
                  {formatDurationFromMs(item.duration)}
                </div>
                <div className="text-[#DFE2E7] text-sm font-[SVN-Gilroy] line-clamp-3">
                  {item.text || "Đoạn dẫn"}
                </div>
              </div>
            ))}
        </div>
      </div>
      <div
        className={clsx(
          "fixed -right-[420px] top-[72px] w-[420px] overscroll-contain transition-all bg-[#272C36] h-[calc(100vh-72px)] max-h-[calc(100vh-72px)] flex flex-col overflow-y-auto",
          {
            "!right-0": !!isCommentShow,
          }
        )}
      >
        <div className="relative text-[#171A20] pt-5 p-6 pb-4 z-10 ">
          <div className="flex text-white items-center justify-between">
            <div className="font-[SVN-Gilroy] font-bold text-xl">
              Bình luận{" "}
              <span className="font-light">
                ({book?.insight?.commentCount ?? 0})
              </span>
            </div>
            <div className="cursor-pointer" onClick={handleCommentClick}>
              <img src={Images.CloseIcon.default} alt="" />
            </div>
          </div>
          <hr className="absolute w-[380px] left-[18px] bottom-1 border-t-[#4E586C]" />
        </div>

        <div className="pl-4 flex-1 overflow-y-auto">
          <CommentItem
            comment={comment}
            onReplyComment={handleReply}
            onLikeComment={handleLikeComment}
            onDeleteComment={handleDeleteComment}
            onEditComment={handleEditComment}
          />
        </div>

        <div className="relative px-2 py-4  ">
          <Formik
            initialValues={{ content: "" }}
            onSubmit={onSubmit}
            validateOnBlur
            validateOnChange
            enableReinitialize
          >
            <Form>
              <hr className="absolute w-[380px] left-[18px] top-0 border-t-[#4E586C]" />
              <div className="flex items-center justify-evenly">
                <FormControl name="content">
                  <Input
                    className="!w-[280px]"
                    inputClassName="!bg-[#506079] !h-[40px] !text-white font-[SVN-Gilroy]"
                    placeholder="Thêm bình luận"
                  />
                </FormControl>
                <Button
                  width="auto"
                  type="submit"
                  className="!w-[80px] !h-9 font-bold !text-[#6f98f0] !shadow-none !bg-transparent"
                  label="Đăng"
                  labelClassName="text-sm"
                />
              </div>
            </Form>
          </Formik>
        </div>
      </div>

      <audio ref={backgroundAudioRef} hidden>
        <source src={backgroundAudioLink} type="" />
      </audio>
      <audio ref={chapterAudioRef} hidden>
        <source src={chapterAudioLink} type="" />
      </audio>
    </div>
  );
}

export default Reading;
